import { ReactElement, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { HiOutlineArrowLeft } from 'react-icons/hi2';
import Spinner from './Spinner';
import { cn } from '../utils/cn';

interface SectionHeaderProps {
  title: string;
  isLoading?: boolean;
  backLink?: {
    to: string;
    text: string;
  };
  actions?: ReactNode;
  className?: string;
  titleClassName?: string;
  variant?: 'page' | 'section';
  inset?: boolean;
}

const PAGE_TITLE_CLASSES =
  'cr-text-2xl lg:cr-text-3xl cr-font-extrabold cr-bg-gradient-to-r cr-from-blue-900 cr-to-blue-500 '
  + 'cr-bg-clip-text cr-text-transparent';

export default function SectionHeader({
  title,
  isLoading = false,
  backLink,
  actions,
  className = '',
  titleClassName = '',
  variant = 'page',
  inset = false,
}: SectionHeaderProps): ReactElement {
  const baseClasses = 'cr-mb-4 cr-mt-3 cr-w-full lg:cr-mb-8 lg:cr-mt-6';
  const insetClasses = inset ? 'cr-rounded-lg cr-bg-white cr-px-4 cr-py-3 lg:cr-px-6 lg:cr-py-4' : '';
  const insetStyle = inset
    ? { boxShadow: 'inset 0 2px 6px rgba(0, 0, 0, 0.12), inset 0 1px 3px rgba(0, 0, 0, 0.08)' }
    : {};

  const variantClasses = variant === 'page' ? PAGE_TITLE_CLASSES : '';

  return (
    <div
      className={cn(baseClasses, insetClasses, className)}
      style={insetStyle}
    >
      <div className="cr-flex cr-items-center cr-justify-between cr-gap-3 lg:cr-gap-8">
        <h1 className={cn(
          'font-montserrat cr-flex cr-flex-row cr-items-center cr-text-lg cr-uppercase cr-text-blue-900 lg:cr-text-xl',
          variantClasses,
          titleClassName
        )}>
          {title}
          <span className="cr-ml-2">
            <Spinner isFetching={isLoading} />
          </span>
        </h1>
        {/* Decorative rule — it only has room to read as a rule from lg up. */}
        <div className="cr-hidden cr-flex-1 cr-border-b cr-border-gray-300 lg:cr-mx-6 lg:cr-block"></div>
        <div className="cr-flex cr-shrink-0 cr-items-center cr-gap-2">
          {actions}
          {backLink && (
            <Link
              to={backLink.to}
              className="cr-flex cr-items-center cr-gap-x-1.5 cr-whitespace-nowrap cr-rounded-md cr-bg-gray-200 cr-px-3 cr-py-1.5 cr-font-main cr-text-sm cr-font-semibold cr-text-blue-900 cr-no-underline cr-transition-colors cr-duration-200 hover:cr-bg-cirrusBlue-50"
            >
              <HiOutlineArrowLeft className="cr-h-4 cr-w-4" />
              {backLink.text}
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
